import React, { useState, useEffect } from 'react';
import { IUser } from '../../types/User';
import { 
    XMarkIcon, 
    UserIcon, 
    EnvelopeIcon, 
    LockClosedIcon, 
    ShieldCheckIcon,
    InformationCircleIcon
} from '@heroicons/react/24/outline';
import styles from './UserModal.module.css'; // Usaremos el nuevo CSS mejorado

interface UserModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (userData: any) => void;
    initialData?: IUser | null;
    isEditing: boolean;
}

const UserModal: React.FC<UserModalProps> = ({ isOpen, onClose, onSubmit, initialData, isEditing }) => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        rol: 'observador'
    });

    useEffect(() => {
        if (isOpen) { // Resetear solo cuando se abre
            if (initialData && isEditing) {
                setFormData({
                    name: initialData.name,
                    email: initialData.email,
                    password: '',
                    rol: initialData.rol
                });
            } else {
                setFormData({ name: '', email: '', password: '', rol: 'observador' });
            }
        }
    }, [initialData, isEditing, isOpen]);

    if (!isOpen) return null;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const dataToSend = { ...formData };
        if (isEditing && !dataToSend.password) {
            delete (dataToSend as any).password;
        }
        onSubmit(dataToSend);
    };

    return (
        <div className={styles.modalOverlay} onClick={onClose}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                {/* Header con gradiente */}
                <div className={styles.modalHeader}>
                    <div className={styles.headerContent}>
                        <h2 className={styles.modalTitle}>
                            {isEditing ? 'Editar Usuario' : 'Nuevo Usuario'}
                        </h2>
                        <p className={styles.modalSubtitle}>
                            {isEditing ? 'Actualiza la información y permisos del usuario.' : 'Crea un nuevo usuario y asigna sus permisos.'}
                        </p>
                    </div>
                    <button onClick={onClose} className={styles.closeButton} title="Cerrar">
                        <XMarkIcon className={styles.closeIcon} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className={styles.modalBody}>
                    {/* Sección de Datos Personales */}
                    <div className={styles.section}>
                        <div className={styles.formGroup}>
                            <label htmlFor="name" className={styles.formLabel}>
                                <UserIcon className={styles.labelIcon} />
                                Nombre Completo *
                            </label>
                            <input 
                                type="text" 
                                id="name"
                                name="name" 
                                value={formData.name} 
                                onChange={handleChange} 
                                className={styles.formInput}
                                placeholder="Ej: Juan Pérez"
                                required 
                            />
                        </div>

                        <div className={styles.formGroup}>
                            <label htmlFor="email" className={styles.formLabel}>
                                <EnvelopeIcon className={styles.labelIcon} />
                                Correo Electrónico *
                            </label>
                            <input 
                                type="email" 
                                id="email"
                                name="email" 
                                value={formData.email} 
                                onChange={handleChange} 
                                className={styles.formInput}
                                placeholder="ejemplo@empresa.com"
                                required 
                            />
                        </div>
                    </div>

                    {/* Sección de Seguridad y Roles */}
                    <div className={styles.section}>
                        <div className={styles.formGroup}>
                            <label htmlFor="password" className={styles.formLabel}>
                                <LockClosedIcon className={styles.labelIcon} />
                                Contraseña {isEditing ? '(Opcional)' : '*'}
                            </label>
                            <input 
                                type="password" 
                                id="password"
                                name="password" 
                                value={formData.password} 
                                onChange={handleChange} 
                                className={styles.formInput}
                                placeholder={isEditing ? "••••••••" : "Mínimo 6 caracteres"}
                                required={!isEditing} 
                                minLength={6} 
                            />
                            {isEditing && (
                                <p className={styles.helperText}>Dejar en blanco para mantener la contraseña actual.</p>
                            )}
                        </div>

                        <div className={styles.formGroup}>
                            <label htmlFor="rol" className={styles.formLabel}>
                                <ShieldCheckIcon className={styles.labelIcon} />
                                Rol de Usuario *
                            </label>
                            <div className={styles.selectWrapper}>
                                <select 
                                    id="rol"
                                    name="rol" 
                                    value={formData.rol} 
                                    onChange={handleChange} 
                                    className={styles.formSelect}
                                >
                                    <option value="observador">Observador (Solo lectura)</option>
                                    <option value="editor">Editor (Gestionar contenido)</option>
                                    <option value="administrador">Administrador (Acceso total)</option>
                                </select>
                            </div>
                            
                            {/* Info del rol seleccionado */}
                            <div className={`${styles.roleInfo} ${styles[formData.rol]}`}>
                                <InformationCircleIcon className={styles.infoIcon} />
                                <p>
                                    {formData.rol === 'administrador' && "Puede gestionar usuarios, proyectos, artículos y configuraciones."}
                                    {formData.rol === 'editor' && "Puede crear y editar proyectos y artículos. No puede eliminar contenido ni gestionar usuarios."}
                                    {formData.rol === 'observador' && "Solo puede ver el contenido. No puede realizar cambios."}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className={styles.modalFooter}>
                        <button type="button" onClick={onClose} className={styles.cancelButton}>
                            Cancelar
                        </button>
                        <button type="submit" className={styles.submitButton}>
                            {isEditing ? 'Guardar Cambios' : 'Crear Usuario'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default UserModal;